import {
  AccountDetailResponsePayload,
  AchRelationshipResponsePayload,
  CashDirection,
  SearchCashTransfersResponsePayload
} from '@Types'
import isEmpty from 'lodash.isempty'
import isNil from 'lodash.isnil'
import { DateTime } from 'luxon'
import React, { useEffect, useState } from 'react'
import { BasicTransferDetails } from '../BasicTransferDetails'
import { buildAccountText, buildAchRelationshipText } from '../../utils/optionBuilders'
import { TransferForm, AccountDirections } from '@Types'
import { useFormatNumber } from '../../utils'
import CancelTransferModalWireDetails from '../CancelTransferModalWireDetails'
import CancelTransferModalCheckDetails from '../CancelTransferModalCheckDetails'
import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooterAction,
  LabeledTextarea
} from '@apex/component-library'
import { useCancelTransfer } from '../../api/useCancelTransfer'
import { useFormik } from 'formik'
import * as Yup from 'yup'

export interface Props {
  accountNumber: string
  transferData?: SearchCashTransfersResponsePayload | null
  type: string
  achRelationships?: AchRelationshipResponsePayload[] | null
  accountDetails?: AccountDetailResponsePayload | null
  isOpen: boolean
  onClose: () => void
}

export const CancelTransferModal = ({
  transferData,
  type,
  achRelationships,
  accountDetails,
  accountNumber,
  isOpen,
  onClose
}: Props) => {
  const formatNumber = useFormatNumber()
  const { mutate, isLoading: isCanceling } = useCancelTransfer(transferData?.id!, accountNumber)

  const formik = useFormik({
    initialValues: {
      comment: ''
    },
    validationSchema: Yup.object({
      comment: Yup.string().max(100, 'Must be 100 characters or less')
    }),
    onSubmit: () => {}
  })

  const cancelTransfer = () => {
    mutate(
      { comment: formik.values.comment },
      {
        onSuccess: () => {
          onClose()
          formik.resetForm()
        }
      }
    )
  }

  const [details, setDetails] = useState<TransferForm>({
    transferDate: '',
    transferDetails: {
      transferType: {
        raw: '',
        display: ''
      },
      transferAmount: {
        display: '',
        raw: ''
      },
      accounts: {
        direction: '',
        from: {
          raw: '',
          display: ''
        },
        to: {
          raw: '',
          display: ''
        }
      }
    }
  })

  const buildAccounts = (
    transfer?: SearchCashTransfersResponsePayload | null,
    achRelationships?: AchRelationshipResponsePayload[] | null,
    account?: AccountDetailResponsePayload | null
  ) => {
    // use account number and relationship id with the loaded ach relationships to build
    // use cash direction to set which is from / to account
    if (isNil(transfer?.direction)) {
      return {}
    }
    const achRel =
      achRelationships?.filter((relationship) => relationship?.id === transfer?.achDetails?.relationshipId) || []

    const achRelText = isEmpty(achRel) ? transfer?.achDetails?.relationshipId : buildAchRelationshipText(achRel[0])

    const apexAccount = buildAccountText(accountNumber, account?.accountTitle)

    const achRelDisplay = {
      raw: transfer?.achDetails?.relationshipId,
      display: achRelText || ''
    }
    const accountDisplay = {
      raw: accountNumber,
      display: apexAccount
    }
    let from = {}
    let to = {}

    if (transfer?.direction === CashDirection.Withdrawal) {
      from = accountDisplay
      to = achRelDisplay
    }
    if (transfer?.direction === CashDirection.Deposit) {
      from = achRelDisplay
      to = accountDisplay
    }
    if (type.includes('CASH JOURNAL') && !isNil(transferData) && !isNil(transferData?.cashJournalDetails)) {
      return {
        from: {
          display:
            transferData?.cashJournalDetails.account === accountNumber
              ? buildAccountText(transferData?.cashJournalDetails.account, account?.accountTitle)
              : transferData?.cashJournalDetails.account
        },
        to: { display: transferData?.cashJournalDetails.contraAccountId }
      }
    } else {
      return {
        from: from,
        to: to
      }
    }
  }

  const buildTransferDetails = () => {
    const submittedDate = transferData?.initiationTime
      ? DateTime.fromISO(transferData?.initiationTime).toLocaleString(DateTime.DATE_SHORT)
      : ''

    const amount =
      isNil(transferData?.amount) || isNaN(transferData?.amount as number)
        ? ''
        : formatNumber(transferData?.amount as number, {
            style: 'currency',
            currency: 'USD'
          })
    const data: TransferForm = {
      transferDate: submittedDate,
      transferDetails: {
        transferType: {
          raw: type,
          display: type
        },
        transferAmount: {
          display: amount,
          raw: transferData?.amount as number
        },
        accounts: buildAccounts(transferData, achRelationships, accountDetails) as AccountDirections,
        ...(!isEmpty(transferData?.wireDetails) && { wireDetails: transferData?.wireDetails }),
        ...(!isEmpty(transferData?.checkDetails) && { checkDetails: transferData?.checkDetails })
      }
    }
    setDetails(data)
  }

  useEffect(() => {
    buildTransferDetails()
    // eslint-disable-next-line
  }, [achRelationships, accountDetails, transferData])

  const closeAndResetModal = () => {
    onClose()
    formik.resetForm()
  }

  return (
    <Modal isCentered isOpen={isOpen} onClose={closeAndResetModal}>
      <ModalOverlay>
        <ModalContent w='100%' maxW='600px'>
          <ModalHeader textStyle='h1'>Cancel Transfer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack pb={8} spacing={8}>
              <Text textStyle='lead'>Please confirm you want to cancel the following transfer.</Text>
              <Text textStyle='h5'>Transfer Details </Text>
              {isEmpty(details.transferDetails?.wireDetails) && isEmpty(details.transferDetails?.checkDetails) && (
                <BasicTransferDetails fields={details} />
              )}
              {!isEmpty(details.transferDetails?.wireDetails) && <CancelTransferModalWireDetails fields={details} />}
              {!isEmpty(details.transferDetails?.checkDetails) && <CancelTransferModalCheckDetails fields={details} />}
              <LabeledTextarea
                name='comment'
                data-testid='cancel-transfer-comment'
                label='Comment'
                aria-label='Comment'
                rows={2}
                width='100%'
                onBlur={formik.handleBlur}
                value={formik.values.comment}
                onChange={formik.handleChange}
                isInvalid={formik.touched.comment && !!formik.errors?.comment}
                errorMessage={formik.errors?.comment}
              />
            </Stack>
          </ModalBody>
          <ModalFooterAction>
            <Flex>
              <Box ml='auto'>
                <Button variant='white' onClick={closeAndResetModal}>
                  Close
                </Button>
                <Button
                  data-testid='modal-cancel-transfer-button'
                  variant='negative'
                  ml={3}
                  onClick={cancelTransfer}
                  isLoading={isCanceling}
                  isDisabled={!formik.isValid || isCanceling}
                >
                  Cancel Transfer
                </Button>
              </Box>
            </Flex>
          </ModalFooterAction>
        </ModalContent>
      </ModalOverlay>
    </Modal>
  )
}
